import React from 'react';
import { motion, type MotionProps } from 'framer-motion';
import { cn } from '../../lib/utils';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  motionProps?: MotionProps;
  variant?: 'default' | 'hover' | 'interactive';
}

export function Card({ 
  children, 
  className, 
  motionProps,
  variant = 'default',
  ...props 
}: CardProps) {
  const baseClasses = "rounded-2xl overflow-hidden bg-gradient-to-br from-[#0054fa]/70 via-[#002787]/70 to-[#030a21]/70";
  const variantClasses = {
    default: "relative",
    hover: "relative transition-transform hover:-translate-y-1",
    interactive: "relative cursor-pointer transition-all hover:shadow-lg hover:shadow-blue-500/20"
  };
  
  const cardContent = (
    <div 
      className={cn(baseClasses, variantClasses[variant], "flex flex-col", className)} 
      style={{ 
        pointerEvents: variant === 'interactive' ? 'auto' : 'none',
      }}
      {...props}
    >
      <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,white,rgba(255,255,255,0.6))]" />
      <div className="relative flex-1 flex flex-col" style={{ pointerEvents: 'auto' }}>
        {children}
      </div>
    </div>
  );

  if (motionProps) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="h-full"
        {...motionProps}
      >
        {cardContent}
      </motion.div>
    );
  }

  return cardContent;
}

Card.Header = function CardHeader({ 
  className, 
  children 
}: { 
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("relative p-6", className)}>
      {children}
    </div>
  );
};

Card.Content = function CardContent({ 
  className, 
  children 
}: { 
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("p-6 pt-0 flex-1 flex flex-col", className)}>
      {children}
    </div>
  );
};

Card.Footer = function CardFooter({ 
  className, 
  children 
}: { 
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("p-6 border-t border-white/10 mt-auto", className)}>
      {children}
    </div>
  );
};